import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  runComplianceChecks,
  getComplianceChecks,
  getComplianceChecksSummary,
  getPolicyFreshness,
  getPolicyReviewQueue,
  resolvePolicyReview,
} from "../api.js";

/**
 * PoliciesPage — Policy & compliance overview across all IOs.
 *
 * Shows the latest Lawspeaker compliance verdict for every ingested IO, with
 * KPI cards (pass / warn / block / check error), a "Run all checks" action,
 * verdict filters, and expandable breach feedback per IO (issues, severity,
 * policy page links, recommendations). IOs flagged in breach link straight
 * into their review pane.
 *
 * Verdict record shape (persisted by POST /api/v1/compliance/checks/run):
 * {
 *   io_number, title, categories, platforms, countries, us_states,
 *   checked_at, verdict: "pass"|"warn"|"block"|"check_error", score,
 *   audit_id, recommendations?: string[],
 *   issues: [{ source, scope, severity, code, message, policy_url }],
 *   error?: string,
 * }
 */

const VERDICT_ORDER = ["block", "check_error", "warn", "pass"];

/**
 * ReviewQueue — Lawspeaker pending_review proposals from the reverify
 * crawler. Each row is a place where the cited source page apparently
 * disagrees with the corpus. Resolving is a human decision: "Apply" merges
 * the proposal into the live corpus (change takes effect on the next
 * verdict), "Reject" drops it. Failures degrade to inline messages so the
 * rest of the Policies page keeps working.
 */
function ReviewQueue({ onChanged }) {
  const [reviews, setReviews] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState(false);

  const loadQueue = useCallback(async () => {
    try {
      const res = await getPolicyReviewQueue();
      const d = res?.data || res;
      setReviews(Array.isArray(d?.reviews) ? d.reviews : []);
    } catch {
      setReviews([]);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const resolve = useCallback(
    async (id, action) => {
      setBusyId(id);
      setMsg(null);
      try {
        await resolvePolicyReview(id, action);
        setMsg(action === "apply" ? "Proposal applied to the corpus." : "Proposal rejected.");
        await loadQueue();
        onChanged?.();
      } catch (err) {
        setMsg(err.message || "Resolve failed");
      } finally {
        setBusyId(null);
      }
    },
    [loadQueue, onChanged],
  );

  if (!reviews.length) return null;

  return (
    <div className="policies-review-queue">
      <button className="policies-review-toggle" onClick={() => setOpen((o) => !o)}>
        <span className={`verdict-label verdict-label-warn`}>Review queue</span>
        {reviews.length} crawler proposal{reviews.length === 1 ? "" : "s"} awaiting decision
        <span aria-hidden>{open ? " ▾" : " ▸"}</span>
      </button>

      {msg && <div className="policies-run-msg">{msg}</div>}

      {open && (
        <div className="policies-review-list">
          {reviews.map((r) => (
            <div key={r.id} className="policies-review-row">
              <div className="policies-review-main">
                <span className="policies-io mono">{r.platform_or_jurisdiction}</span>
                <span className="policies-title">{r.category}</span>
                <span className={`verdict-label verdict-label-warn`}>{r.status}</span>
              </div>
              <p className="policies-review-notes">{r.conditions_and_notes}</p>
              <div className="policies-review-meta">
                <a href={r.policy_url} target="_blank" rel="noreferrer">
                  cited source
                </a>
                <span>· crawler {r.created_at ? new Date(r.created_at + "Z").toLocaleString() : ""}</span>
              </div>
              <div className="policies-review-actions">
                <button
                  className="btn btn-sm btn-primary"
                  disabled={busyId === r.id}
                  onClick={() => resolve(r.id, "apply")}
                >
                  {busyId === r.id ? "…" : "Apply"}
                </button>
                <button className="btn btn-sm btn-ghost" disabled={busyId === r.id} onClick={() => resolve(r.id, "reject")}>
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VERDICT_META = {
  pass: { label: "Pass", cls: "pass", desc: "Clear to proceed" },
  warn: { label: "Warn", cls: "warn", desc: "Review before launch" },
  block: { label: "Block", cls: "block", desc: "Campaign will be rejected" },
  check_error: { label: "Check error", cls: "check_error", desc: "Compliance service unreachable" },
};

function VerdictDot({ verdict, size = 12 }) {
  const color =
    verdict === "block" ? "#5a5a5a" :
    verdict === "check_error" ? "#7a7a7a" :
    verdict === "warn" ? "#b0b0b0" : "#ededed";
  return (
    <span
      className="verdict-dot"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        marginRight: 6,
        verticalAlign: "middle",
      }}
      aria-label={`verdict: ${verdict}`}
    />
  );
}

function KpiCard({ label, value, cls, sub }) {
  return (
    <div className={`policies-kpi policies-kpi-${cls}`}>
      <div className="policies-kpi-value">
        <VerdictDot verdict={cls} size={14} />
        {value}
      </div>
      <div className="policies-kpi-label">{label}</div>
      <div className="policies-kpi-sub">{sub}</div>
    </div>
  );
}

function CheckRow({ check }) {
  const [open, setOpen] = useState(false);
  const meta = VERDICT_META[check.verdict] || { label: check.verdict, cls: "warn", desc: "" };
  const flagged = check.verdict === "block" || check.verdict === "warn" || check.verdict === "check_error";
  const issueCount = check.issues?.length ?? 0;

  // Deep-link into the IO review page with the warnings drawer already open.
  const reviewHref = `/ios/${encodeURIComponent(check.io_number)}?drawer=warnings`;

  return (
    <div className={`policies-row policies-row-${meta.cls}`}>
      <div className="policies-row-main" onClick={() => setOpen((o) => !o)}>
        <span className={`verdict-label verdict-label-${meta.cls}`}>{meta.label}</span>
        <span className="policies-io mono">{check.io_number}</span>
        <span className="policies-title">{check.title}</span>
        <span className="policies-flags">
          {flagged ? (
            <>
              {issueCount} {issueCount === 1 ? "flag" : "flags"}
            </>
          ) : (
            "Clear"
          )}
        </span>
        <span className="policies-checked">{new Date(check.checked_at).toLocaleString()}</span>
        <button className="btn-icon" aria-label={open ? "collapse" : "expand"}>
          {open ? "▾" : "▸"}
        </button>
      </div>

      <div className="policies-row-tags">
        {(check.platforms || []).map((p) => (
          <span key={p} className="policies-tag">
            {p}
          </span>
        ))}
        {(check.categories || []).map((c) => (
          <span key={c} className="policies-tag policies-tag-muted">
            {c}
          </span>
        ))}
      </div>

      {open && (
        <div className="policies-row-detail">
          <div className="policies-row-actions">
            {/* The warnings and "What to fix" detail live on the IO page, in the
                right-hand drawer — not here, so this list stays an index. */}
            <Link className="btn btn-sm" to={reviewHref} onClick={(e) => e.stopPropagation()}>
              Open IO review
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PoliciesPage() {
  const [checks, setChecks] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("");
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [freshness, setFreshness] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // request() unwraps the { data } envelope: list endpoints come back as
      // the bare array, summary/run as plain objects. Handle both shapes.
      const list = await getComplianceChecks({ verdict: filter || undefined, limit: 200 });
      const rows = Array.isArray(list) ? list : (list.data || []);
      const totalCount = Array.isArray(list) ? rows.length : (list.total ?? rows.length);
      setChecks(rows);
      setTotal(totalCount);
      const s = await getComplianceChecksSummary();
      setSummary(s?.data || s);
      // Freshness badge is non-critical: a failure here must not break the page.
      try {
        const f = await getPolicyFreshness();
        setFreshness(f?.data || f);
      } catch {
        setFreshness(null);
      }
    } catch (err) {
      setError(err.message || "Failed to load compliance checks");
    } finally {
      setLoaded(true);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunAll = useCallback(async () => {
    setRunning(true);
    setRunMsg(null);
    setError(null);
    try {
      const res = await runComplianceChecks();
      const d = res?.data || res;
      if (d.skipped) {
        setRunMsg(`Compliance gate is disabled (${d.skipped}), no checks run.`);
      } else {
        setRunMsg(`Checked ${d.checked} IO${d.checked === 1 ? "" : "s"}.`);
      }
      await load();
    } catch (err) {
      setError(err.message || "Compliance check run failed");
    } finally {
      setRunning(false);
    }
  }, [load]);

  const counts = summary?.by_verdict || { pass: 0, warn: 0, block: 0, check_error: 0 };

  return (
    <div className="policies-page container">
      <div className="page-header">
        <div>
          <h2>Policy checks</h2>
          <p className="page-sub">
            Every IO is checked against advertising policy rules before approval and
            campaign generation. Flagged IOs need review before they can proceed.
          </p>
        </div>
        <div className="policies-actions">
          {runMsg && <span className="policies-run-msg">{runMsg}</span>}
          <button className="btn" onClick={handleRunAll} disabled={running}>
            {running ? "Checking all IOs…" : "Run all checks"}
          </button>
        </div>
      </div>

      {freshness?.freshness && (
        <div className="policies-freshness" title="Corpus re-verification SLO: 30 days for political rules, 90 days for everything else.">
          {freshness.freshness.stale > 0
            ? `${freshness.freshness.stale} of ${freshness.freshness.total} policy rules are past their re-verification window`
            : "All policy rules verified within their re-verification window"}
          {freshness.freshness.uncited > 0 &&
            ` · ${freshness.freshness.uncited} rule${freshness.freshness.uncited === 1 ? "" : "s"} missing citations`}
          {" · "}
          <span>
            SLO: {freshness.freshness.slo_days?.political ?? 30}d political / {freshness.freshness.slo_days?.standard ?? 90}d standard
          </span>
        </div>
      )}

      <div className="policies-kpis">
        <KpiCard label="Blocked" value={counts.block} cls="block" sub={VERDICT_META.block.desc} />
        <KpiCard label="Warnings" value={counts.warn} cls="warn" sub={VERDICT_META.warn.desc} />
        <KpiCard label="Passed" value={counts.pass} cls="pass" sub={VERDICT_META.pass.desc} />
        <KpiCard
          label="Check errors"
          value={counts.check_error}
          cls="check_error"
          sub={VERDICT_META.check_error.desc}
        />
      </div>

      <ReviewQueue onChanged={load} />

      <div className="policies-filters">
        <button
          className={`btn btn-sm ${filter === "" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setFilter("")}
        >
          All ({summary?.total_checked ?? 0})
        </button>
        {VERDICT_ORDER.map((v) => (
          <button
            key={v}
            className={`btn btn-sm ${filter === v ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setFilter(v)}
          >
            {VERDICT_META[v].label} ({counts[v] ?? 0})
          </button>
        ))}
      </div>

      {error && <div className="compliance-error">{error}</div>}

      {!loaded && <div className="policies-loading">Loading policy checks…</div>}

      {loaded && checks.length === 0 && (
        <div className="policies-empty">
          <p>No compliance checks recorded yet.</p>
          <p className="compliance-hint">
            Checks run automatically when an IO is ingested, or use "Run all checks"
            above to check every IO now.
          </p>
        </div>
      )}

      {checks.length > 0 && (
        <div className="policies-list">
          {checks.map((check) => (
            <CheckRow key={check.io_number} check={check} />
          ))}
        </div>
      )}

      {loaded && total > checks.length && (
        <div className="policies-more">
          Showing {checks.length} of {total} checks. Narrow the filter to see more.
        </div>
      )}
    </div>
  );
}